import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { NgOptimizedImage } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatBadge } from '@angular/material/badge';
import { MatIconButton } from '@angular/material/button';
import { Router, RouterLink } from '@angular/router';
import {
  ButtonComponent,
  Icon,
  IconButtonComponent,
  IconButtonIconFontSize,
  IconComponent,
} from '@claas/claas-ui-components';
import { TranslateModule } from '@ngx-translate/core';
import { ClickOutside } from 'ngxtension/click-outside';
import { Subscription } from 'rxjs';
import { PartsShopResponsiveCartTableComponent } from '@cde-fe-app/app/shared/components/parts-shop/parts-shop-responsive-cart-table/parts-shop-responsive-cart-table.component';
import { HasPermissionsDirective } from '@cde-fe-app/app/shared/directive/hasPermissions.directive';
import { OrderEntry } from '@cde-fe-app/app/shared/models/parts-shop/parts-shop-cart.model';
import { partsShopCartStore } from '@cde-fe-app/app/shared/state/parts-shop/parts-shop-cart/parts-shop-cart.store';
import { PLATTFORM_MANAGE_PERMISSION } from '@cde-fe-app/app/shared/utils/permission';

@Component({
  selector: 'app-mini-cart',
  imports: [
    RouterLink,
    MatBadge,
    IconComponent,
    MatIconButton,
    PartsShopResponsiveCartTableComponent,
    TranslateModule,
    IconButtonComponent,
    ButtonComponent,
    ClickOutside,
    NgOptimizedImage,
    HasPermissionsDirective,
  ],
  templateUrl: './mini-cart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './mini-cart.component.scss',
})
export class MiniCartComponent implements OnInit, OnDestroy {
  readonly cartStore = inject(partsShopCartStore);
  readonly breakpointObserver = inject(BreakpointObserver);
  readonly router = inject(Router);

  smallDisplay = signal<boolean>(false);

  /* eslint-disable @typescript-eslint/member-ordering */
  readonly icon: Icon = {
    iconId: '648550',
    namespace: 'cc',
    size: 20,
    iconStyle: 'bold',
  };
  readonly closeIcon: Icon = {
    iconId: '648548',
    namespace: 'cc',
    size: 24,
    iconStyle: 'bold',
  };
  readonly cartIcon: Icon = {
    iconId: '648469',
    namespace: 'cc',
    size: 24,
    iconStyle: 'bold',
  };
  readonly fastInputIcon: Icon = {
    iconId: '648541',
    namespace: 'cc',
    iconStyle: 'bold',
    size: 20,
  };
  /* eslint-enable @typescript-eslint/member-ordering */

  protected readonly platformManage = PLATTFORM_MANAGE_PERMISSION;
  protected readonly iconButtonIconFontSize = IconButtonIconFontSize;
  private subscriptions: Subscription[] = [];

  get openDrawer() {
    return this.cartStore.openMinicartDrawer;
  }

  ngOnInit() {
    this.subscriptions.push(
      this.breakpointObserver.observe([Breakpoints.Small, Breakpoints.XSmall]).subscribe((state: BreakpointState) => {
        this.smallDisplay.set(state.matches);
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s?.unsubscribe());
  }

  closeDrawer() {
    this.cartStore.setOpenMinicartDrawer(false);
  }

  remove(entries: number[]) {
    if (entries.length) {
      this.cartStore.removeFromActiveCart({ orderEntries: entries.map(entryNumber => ({ entryNumber: entryNumber })) });
    }
  }

  updateEntry(entry: OrderEntry) {
    if (entry.entryNumber !== undefined) {
      if (entry.quantity) {
        this.cartStore.updateEntryFromActiveCart({
          orderEntries: [
            {
              entryNumber: entry.entryNumber,
              product: { code: entry.product?.code ?? '' },
              quantity: entry.quantity ?? 0,
            },
          ],
        });
      } else {
        this.cartStore.removeFromActiveCart({ orderEntries: [entry] });
      }
    }
  }

  navigateToCart(action?: string) {
    void this.router.navigate(['', 'parts-shop', 'cart'], { state: { data: { action: action } } });
    this.closeDrawer();
  }
}
